import { FormEvent, useEffect, useState } from "react";
import { Edit, ImagePlus, Plus, RefreshCw, Save, Search, Star, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminTestimonial,
  AdminTestimonialPayload,
  AdminUser,
  PaginationMeta,
  deleteAdminTestimonial,
  deleteAdminTestimonialImage,
  getCurrentAdmin,
  listAdminTestimonials,
  logout,
  saveAdminTestimonial,
  uploadAdminTestimonialImage,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

type FormState = {
  CustomerName: string;
  CustomerHandle: string;
  Content: string;
  Rating: string;
  AvatarUrl: string;
  IsVerified: boolean;
  IsActive: boolean;
  SortOrder: string;
};

const emptyForm: FormState = {
  CustomerName: "",
  CustomerHandle: "",
  Content: "",
  Rating: "5",
  AvatarUrl: "",
  IsVerified: false,
  IsActive: true,
  SortOrder: "100",
};

function itemToForm(item: AdminTestimonial): FormState {
  return {
    CustomerName: item.CustomerName,
    CustomerHandle: item.CustomerHandle || "",
    Content: item.Content,
    Rating: String(item.Rating || 5),
    AvatarUrl: item.AvatarUrl || "",
    IsVerified: item.IsVerified,
    IsActive: item.IsActive,
    SortOrder: String(item.SortOrder || 100),
  };
}

function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5 text-[#D4A574]">
      {[1, 2, 3, 4, 5].map((value) => (
        <Star key={value} fill={value <= rating ? "currentColor" : "none"} size={14} strokeWidth={1.5} />
      ))}
    </div>
  );
}

export default function AdminTestimonials() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminTestimonial[]>([]);
  const [counts, setCounts] = useState({ Active: 0, Inactive: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<AdminTestimonial | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminTestimonials(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Active: 0, Inactive: 0 });
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "使用者好評資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  function openCreateForm() {
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(item: AdminTestimonial) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  }

  async function handleImageUpload(file: File) {
    setUploadingImage(true);
    setFormMessage("");
    try {
      const previousUrl = form.AvatarUrl;
      const url = await uploadAdminTestimonialImage(file);
      setForm((current) => ({ ...current, AvatarUrl: url }));
      if (previousUrl) {
        await deleteAdminTestimonialImage(previousUrl).catch(() => undefined);
      }
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "圖片上傳失敗，請稍後再試。");
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleRemoveImage() {
    if (form.AvatarUrl) {
      await deleteAdminTestimonialImage(form.AvatarUrl).catch(() => undefined);
    }
    setForm((current) => ({ ...current, AvatarUrl: "" }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.CustomerName.trim()) { setFormMessage("請輸入顧客姓名。"); return; }
    if (!form.Content.trim()) { setFormMessage("請輸入好評內容。"); return; }

    const payload: AdminTestimonialPayload = {
      CustomerName: form.CustomerName.trim(),
      CustomerHandle: form.CustomerHandle.trim() || undefined,
      Content: form.Content.trim(),
      Rating: Number(form.Rating || 5),
      AvatarUrl: form.AvatarUrl.trim() || undefined,
      IsVerified: form.IsVerified,
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 100),
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminTestimonial(editingItem?.TestimonialId || null, payload);
      closeForm();
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "好評儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminTestimonial) {
    const confirmed = await confirm({
      title: "刪除這則好評？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.CustomerName}</strong> 的好評嗎？
          <br />
          刪除後前台首頁也會立即移除，且無法復原。
        </>
      ),
      confirmText: "刪除好評",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminTestimonial(item.TestimonialId);
      if (item.AvatarUrl) {
        await deleteAdminTestimonialImage(item.AvatarUrl).catch(() => undefined);
      }
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "好評刪除失敗。");
    }
  }

  const allItemCount = counts.Active + counts.Inactive || pagination.Total;

  return (
    <AdminLayout sectionLabel="內容管理" title="使用者好評管理" user={currentUser} onLogout={handleLogout}>
      <p className="mb-4 text-xs text-[#7b877f]">
        會顯示於前台首頁的「使用者好評」區塊。請確認內容為真實顧客回饋後再標記為「已驗證顧客」，避免誤導消費者。
      </p>

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="hidden gap-3 md:grid md:flex-1 md:grid-cols-3">
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">全部好評</p>
            <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Active", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">上架中</p>
            <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Active}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Inactive", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">已下架</p>
            <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Inactive}</p>
          </button>
        </div>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="testimonial-status">狀態</label>
          <select id="testimonial-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Active">上架</option>
            <option value="Inactive">下架</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="testimonial-keyword">關鍵字</label>
          <input id="testimonial-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="顧客姓名或內容" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增好評
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[920px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[70px] px-4 py-3">大頭貼</th>
                <th className="w-[320px] px-4 py-3">好評內容</th>
                <th className="w-[90px] px-4 py-3">評分</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[80px] px-4 py-3">排序</th>
                <th className="w-[150px] px-4 py-3">更新時間</th>
                <th className="w-[160px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>沒有符合條件的好評。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.TestimonialId}>
                  <td className="px-4 py-4">
                    {item.AvatarUrl ? (
                      <img alt={item.CustomerName} className="h-12 w-12 rounded-full border border-[#dfe6e1] object-cover" src={item.AvatarUrl} />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#e9f4ec] text-sm font-bold text-[#245438]">{item.CustomerName.slice(0, 1)}</div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <strong className="block text-[#203d32]">{item.CustomerName}</strong>
                      {item.IsVerified && <span className="inline-flex rounded-full bg-[#e9f4ec] px-2 py-0.5 text-[11px] font-bold text-[#245438]">已驗證顧客</span>}
                    </div>
                    {item.CustomerHandle && <div className="mt-0.5 text-xs text-[#9a8a7a]">@{item.CustomerHandle}</div>}
                    <div className="mt-1 line-clamp-2 text-xs text-[#7b877f]">{item.Content}</div>
                  </td>
                  <td className="px-4 py-4"><RatingStars rating={item.Rating} /></td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "上架" : "下架"}</span>
                  </td>
                  <td className="px-4 py-4">{item.SortOrder}</td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">{item.UpdatedAt || "-"}</td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(item)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        <Trash2 size={16} />
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯好評" : "新增好評"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">會顯示於前台首頁的「使用者好評」區塊。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>顧客姓名 <span className="text-[#c3423f]">*</span></span>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.CustomerName} onChange={(event) => setForm((current) => ({ ...current, CustomerName: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>補充標籤（選填）</span>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如暱稱或居住地區" value={form.CustomerHandle} onChange={(event) => setForm((current) => ({ ...current, CustomerHandle: event.target.value }))} />
                </label>
              </div>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>好評內容 <span className="text-[#c3423f]">*</span></span>
                <textarea className="min-h-[110px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Content} onChange={(event) => setForm((current) => ({ ...current, Content: event.target.value }))} />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>評分</span>
                <select className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Rating} onChange={(event) => setForm((current) => ({ ...current, Rating: event.target.value }))}>
                  {[5, 4, 3, 2, 1].map((value) => (
                    <option key={value} value={value}>{value} 顆星</option>
                  ))}
                </select>
              </label>
              <div className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>大頭貼</span>
                <div className="flex items-center gap-4">
                  {form.AvatarUrl ? (
                    <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full border border-[#dfe6e1]">
                      <img alt="顧客大頭貼" className="h-full w-full object-cover" src={form.AvatarUrl} />
                      <button
                        aria-label="移除照片"
                        className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center bg-black/50 text-white"
                        onClick={handleRemoveImage}
                        type="button"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex h-24 w-24 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-full border border-dashed border-[#cfd8d2] text-[#9aa59f] hover:border-[#315746] hover:text-[#315746]">
                      <ImagePlus size={22} />
                      <span className="text-[11px] font-bold">上傳照片</span>
                      <input
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        type="file"
                        onChange={(event) => event.target.files?.[0] && handleImageUpload(event.target.files[0])}
                      />
                    </label>
                  )}
                  <div className="text-xs font-normal text-[#7b877f]">
                    {uploadingImage ? "上傳中..." : "支援 JPG、PNG、WebP、GIF，檔案大小 5MB 以內。留空則顯示姓名首字。"}
                  </div>
                </div>
              </div>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                排序（數字越小越前面）
                <input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
              </label>
              <AdminSwitchCard checked={form.IsVerified} description="標記後前台會顯示「已驗證顧客」徽章，請確認為真實顧客回饋再標記。" label="已驗證顧客" onChange={(IsVerified) => setForm((current) => ({ ...current, IsVerified }))} />
              <AdminSwitchCard checked={form.IsActive} description="下架後不會顯示於前台首頁。" label="上架顯示" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存好評"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
